import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Heading,
  SelectField,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import { api3Get, api3Post } from '../api/api3Client';

const STATUS_FILTERS = [
  { value: 'needs_decision', label: 'Needs decision' },
  { value: 'queued', label: 'Queued — processing' },
  { value: 'needs_manual', label: 'All manual review' },
  { value: 'provisional_async', label: 'Provisional / async pending' },
  { value: 'provisional_revoked', label: 'Provisional revoked' },
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending (all)' },
  { value: 'clear', label: 'Clear' },
  { value: 'fail', label: 'Fail (all)' },
];

const PROVISIONAL_ASYNC_STATUSES = new Set([
  'provisional_clear',
  'license_check_pending',
  'license_valid',
]);

/** Human-readable labels for users.checkr_mvr_status */
const MVR_STATUS_LABELS = {
  new: 'New — not started',
  pending: 'Pending — Trust MVR',
  license_check_pending: 'License check pending',
  license_valid: 'License valid (in-flight migration)',
  provisional_clear: 'Provisional clear — cleared to proceed',
  provisional_revoked: 'Provisional revoked — late fail',
  clear: 'Clear',
  fail: 'Fail',
  not_found: 'Not found',
  manual_check_criminal: 'Manual review — criminal',
  manual_check_mvr: 'Manual review — MVR policy',
  manual_check_mvr_not_found: 'Manual review — record not found',
};

const MVR_STATUS_HINTS = {
  provisional_clear:
    'Driver was cleared to proceed after 30+ minutes waiting on async license/MVR. Insurance and reservation may proceed while Checkr finishes. No admin action required.',
  provisional_revoked:
    'License/MVR failed after provisional clear. Driver is quiet_suspended — return car at end of reservation. Reservations are not auto-cancelled (RS_10, not RS_7).',
  license_check_pending:
    'Async Checkr Trust license check in progress (criminal-first flow). Promotes to provisional_clear after 30 minutes if still pending.',
  license_valid:
    'Legacy in-flight state. Next onboarding POST migrates to clear without re-running criminal.',
  manual_check_criminal:
    'Criminal policy evaluation failed. Use order_mvr to queue license check (TX/GA/FL…) or Trust MVR by license state.',
};

const ACTION_LABELS = {
  approve: 'Approve MVR (set clear)',
  decline: 'Decline (set fail)',
  order_mvr: 'Order async check (license or MVR by state)',
  rerun_mvr: 'Rerun not-found check',
};

const FLEET_STATUS_LABELS = {
  enabled: 'Enabled',
  quiet_suspended: 'Quiet suspended',
  suspended: 'Suspended',
  first_time_quiet_suspended: 'First-time quiet suspended',
};

function queueBadgeStyle(state) {
  if (state === 'awaiting_system') {
    return { background: '#fff3cd', color: '#856404', padding: '2px 6px', borderRadius: '4px' };
  }
  if (state === 'awaiting_admin') {
    return { background: '#fde8e8', color: '#9b1c1c', padding: '2px 6px', borderRadius: '4px' };
  }
  return undefined;
}

function mvrStatusBadgeStyle(status) {
  const st = (status || '').trim();
  if (st === 'provisional_clear') {
    return { background: '#e8f4ea', color: '#1e6b2f', padding: '2px 6px', borderRadius: '4px' };
  }
  if (st === 'provisional_revoked') {
    return { background: '#fde8e8', color: '#9b1c1c', padding: '2px 6px', borderRadius: '4px' };
  }
  if (st === 'license_check_pending' || st === 'license_valid' || st === 'pending') {
    return { background: '#fff3cd', color: '#856404', padding: '2px 6px', borderRadius: '4px' };
  }
  if (st.startsWith('manual_check')) {
    return { background: '#fde8e8', color: '#9b1c1c', padding: '2px 6px', borderRadius: '4px' };
  }
  if (st === 'clear') {
    return { background: '#e8f4ea', color: '#1e6b2f', padding: '2px 6px', borderRadius: '4px' };
  }
  if (st === 'fail') {
    return { background: '#fde8e8', color: '#9b1c1c', padding: '2px 6px', borderRadius: '4px' };
  }
  return undefined;
}

function formatMvrStatusLabel(status) {
  const st = (status || '').trim();
  return MVR_STATUS_LABELS[st] || st || '—';
}

function formatRelativeAge(diffSec) {
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60);
    return `${m} min${m === 1 ? '' : 's'}`;
  }
  if (diffSec < 86400) {
    const h = Math.floor(diffSec / 3600);
    return `${h} hr${h === 1 ? '' : 's'}`;
  }
  const d = Math.floor(diffSec / 86400);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'}`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} mo${mo === 1 ? '' : 's'}`;
  const y = Math.floor(d / 365);
  return `${y} yr${y === 1 ? '' : 's'}`;
}

/** When this driver entered the current checkr_mvr_status (queue / status time). */
function formatStatusSince(ts) {
  if (ts === undefined || ts === null || ts === '') return '—';
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return String(ts);
  const ms = n > 1e12 ? n : n * 1000;
  const date = new Date(ms);
  const diffSec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  const when = date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${when} (${formatRelativeAge(diffSec)} ago)`;
}

function formatJsonBlock(value) {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

function apiFilterFor(filter) {
  // Server-side filters (provisional_async / provisional_revoked) need api3 deploy.
  // Until then, use broader filters + client-side narrowing in filterItemsForStatus.
  if (filter === 'provisional_async') return 'pending';
  if (filter === 'provisional_revoked') return 'fail';
  return filter;
}

function filterItemsForStatus(items, filter) {
  if (filter === 'needs_decision') {
    return items.filter((row) => row.reviewQueueState === 'awaiting_admin');
  }
  if (filter === 'queued') {
    return items.filter((row) => row.reviewQueueState === 'awaiting_system');
  }
  if (filter === 'provisional_async') {
    return items.filter((row) => PROVISIONAL_ASYNC_STATUSES.has(row.checkrMvrStatus));
  }
  if (filter === 'provisional_revoked') {
    return items.filter((row) => row.checkrMvrStatus === 'provisional_revoked');
  }
  return items;
}

function StatusCell({ status }) {
  const st = (status || '').trim();
  const label = formatMvrStatusLabel(st);
  const showRaw = st && st !== label;
  return (
    <span>
      <span style={mvrStatusBadgeStyle(st)}>{label}</span>
      {showRaw ? (
        <span style={{ display: 'block', fontSize: '0.75rem', color: '#666' }}>{st}</span>
      ) : null}
    </span>
  );
}

function DetailStatusAlert({ status }) {
  const st = (status || '').trim();
  const hint = MVR_STATUS_HINTS[st];
  if (!hint) return null;
  const variation = st === 'provisional_revoked' ? 'warning' : 'info';
  return (
    <Alert variation={variation} marginTop="0.75rem">
      {hint}
    </Alert>
  );
}

function actionConfirmLabel(action, detail) {
  const base = ACTION_LABELS[action] || action;
  const lic = [detail?.licenseState, detail?.license].filter(Boolean).join(' ');
  const licBit = lic ? ` License on file: ${lic}.` : '';
  if (action === 'rerun_mvr') {
    return (
      `${base} for driver ${detail.driverId}?${licBit} ` +
      'This sends that license to Checkr now. A cancelled reservation does not block it.'
    );
  }
  if (action === 'order_mvr') {
    return (
      `${base} for driver ${detail.driverId}?${licBit} ` +
      'Trust states send now. CA, UT, and PA still queue for the monitor.'
    );
  }
  return `${base} for driver ${detail.driverId}?`;
}

export default function AdminChecks() {
  const [statusFilter, setStatusFilter] = useState('needs_decision');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [fleetId, setFleetId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  async function loadList(overrideFilter) {
    const filter = typeof overrideFilter === 'string' ? overrideFilter : statusFilter;
    setLoading(true);
    setError('');
    try {
      const res = await api3Get('/admin/portal/checks', {
        statusFilter: apiFilterFor(filter),
        q: query,
        limit: 100,
      });
      setItems(filterItemsForStatus(res.items || [], filter));
    } catch (e) {
      setError(e.message || String(e));
      setItems([]);
    }
    setLoading(false);
  }

  async function loadDetail(driverId) {
    setLoading(true);
    setError('');
    setSelectedId(driverId);
    try {
      const res = await api3Get(`/admin/portal/checks/${driverId}`);
      setDetail(res.check);
      const firstFleet = res.check?.fleetMemberships?.[0]?.fleetId;
      if (firstFleet) setFleetId(String(firstFleet));
    } catch (e) {
      setError(e.message || String(e));
      setDetail(null);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadList();
  }, [statusFilter]);

  async function executeAction(action) {
    if (!detail) return;
    setConfirm({
      action,
      label: actionConfirmLabel(action, detail),
    });
  }

  async function executeConfirm() {
    if (!confirm || !detail) return;
    setActionLoading(true);
    setError('');
    setInfo('');
    try {
      const body = { action: confirm.action };
      if (confirm.action === 'order_mvr' || confirm.action === 'rerun_mvr') {
        body.fleetId = Number(fleetId);
      }
      const res = await api3Post(
        `/admin/portal/checks/${detail.driverId}/manual-action`,
        body
      );
      setConfirm(null);
      if (res?.result === 'mvr_ordered') {
        setInfo(res.message || 'Checkr accepted the order.');
        setStatusFilter('pending');
        await loadDetail(detail.driverId);
        await loadList('pending');
      } else if (res?.applied === false && res?.message) {
        setInfo(res.message);
        setStatusFilter('queued');
        await loadDetail(detail.driverId);
        await loadList('queued');
      } else if (res?.result === 'declined' || res?.result === 'approved') {
        setInfo(`Driver ${res.result}. They will leave manual review lists.`);
        setDetail(null);
        setSelectedId(null);
        await loadList();
      } else {
        await loadDetail(detail.driverId);
        await loadList();
      }
    } catch (e) {
      setConfirm(null);
      setError(e.message || String(e));
    }
    setActionLoading(false);
  }

  const hasQuietSuspend =
    detail?.fleetStatuses?.some((fs) => fs.userStatus === 'quiet_suspended') ||
    detail?.checkrMvrStatus === 'provisional_revoked';

  return (
    <View padding="1rem">
      <Heading level={3}>Admin: Background Checks</Heading>
      <Text fontSize="0.9rem" color="gray" marginBottom="0.5rem">
        Trust criminal-first flow: criminal → async license (TX/GA/FL…) or MVR → provisional clear
        after 30m → final clear or provisional revoked.
      </Text>
      <Flex direction="row" gap="0.5rem" alignItems="flex-end" wrap="wrap">
        <SelectField
          label="Status filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button onClick={() => loadList()} isLoading={loading}>
          Refresh
        </Button>
      </Flex>

      {error ? (
        <Alert variation="error" isDismissible={false} marginTop="1rem">
          {error}
        </Alert>
      ) : null}

      {info ? (
        <Alert variation="info" isDismissible={false} marginTop="1rem">
          {info}
        </Alert>
      ) : null}

      {loading && !detail ? <Loadingmini /> : null}

      <Flex direction="row" gap="1rem" marginTop="1rem" wrap="wrap">
        <View flex="1" minWidth="300px">
          <Heading level={5}>Drivers</Heading>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th align="left">ID</th>
                <th align="left">Name</th>
                <th align="left">MVR status</th>
                <th align="left">Queue</th>
                <th align="left">Status since</th>
                <th align="left">Error</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.driverId}
                  style={{
                    cursor: 'pointer',
                    background: selectedId === row.driverId ? '#eef6ff' : undefined,
                  }}
                  onClick={() => loadDetail(row.driverId)}
                >
                  <td>{row.driverId}</td>
                  <td>{row.displayName}</td>
                  <td>
                    <StatusCell status={row.checkrMvrStatus} />
                  </td>
                  <td>
                    {row.reviewQueueLabel ? (
                      <span style={queueBadgeStyle(row.reviewQueueState)}>
                        {row.reviewQueueLabel}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatStatusSince(row.checkrMvrTimestamp)}</td>
                  <td>{row.mvrError}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </View>

        {detail ? (
          <View flex="1" minWidth="320px">
            <Heading level={5}>Driver {detail.driverId}</Heading>
            <Text marginTop="0.25rem">
              MVR status: <StatusCell status={detail.checkrMvrStatus} />
            </Text>
            {detail.licenseState || detail.license ? (
              <Text marginTop="0.25rem">
                License on file: {[detail.licenseState, detail.license].filter(Boolean).join(' ')}
              </Text>
            ) : null}
            {detail.reviewQueueLabel ? (
              <Text marginTop="0.25rem">
                Queue:{' '}
                <span style={queueBadgeStyle(detail.reviewQueueState)}>
                  {detail.reviewQueueLabel}
                </span>
              </Text>
            ) : null}
            <DetailStatusAlert status={detail.checkrMvrStatus} />
            {hasQuietSuspend ? (
              <Alert variation="warning" marginTop="0.75rem">
                Driver has quiet_suspended fleet status — cannot book new reservations. Finish
                current trip and return car at end of reservation.
              </Alert>
            ) : null}
            <Text marginTop="0.5rem">
              Status since: {formatStatusSince(detail.checkrMvrTimestamp)}
            </Text>
            <Text>Manual source: {detail.manualSource || '—'}</Text>
            <Text>Manual action: {detail.manualAction || '—'}</Text>
            {detail.reviewQueueState === 'awaiting_system' ? (
              <Text marginTop="0.25rem" color="#856404">
                Queued{' '}
                {detail.manualActionUpdatedTs
                  ? formatStatusSince(detail.manualActionUpdatedTs)
                  : ''}
                — waiting for reservation monitor / onboarding to run the check.
              </Text>
            ) : null}
            <Text>Error: {detail.mvrError || '—'}</Text>

            {detail.fleetStatuses?.length ? (
              <View marginTop="1rem">
                <Heading level={6}>Fleet status (latest per fleet)</Heading>
                <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
                  {detail.fleetStatuses.map((fs) => (
                    <li key={`${fs.fleetId}-${fs.userStatus}`}>
                      {fs.fleetCode || fs.fleetId}:{' '}
                      {FLEET_STATUS_LABELS[fs.userStatus] || fs.userStatus}
                    </li>
                  ))}
                </ul>
              </View>
            ) : null}

            {detail.manualReviewPayload ? (
              <View marginTop="1rem">
                <Heading level={6}>
                  Manual review check output
                  {detail.manualReviewPayload.checkType
                    ? ` (${detail.manualReviewPayload.checkType})`
                    : ''}
                </Heading>
                <Text fontSize="0.85rem" color="gray">
                  Check ID: {detail.manualReviewPayload.checkId || '—'}
                  {detail.manualReviewPayload.createTimestamp
                    ? ` · activity ${detail.manualReviewPayload.activityId}`
                    : ''}
                </Text>
                <View
                  marginTop="0.5rem"
                  padding="0.75rem"
                  backgroundColor="#f4f4f4"
                  borderRadius="4px"
                  style={{
                    maxHeight: '360px',
                    overflow: 'auto',
                    border: '1px solid #ddd',
                  }}
                >
                  {detail.manualReviewPayload.request ? (
                    <>
                      <Text fontSize="0.8rem" fontWeight="bold">
                        Request
                      </Text>
                      <pre
                        style={{
                          margin: '0.25rem 0 0.75rem',
                          fontSize: '11px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatJsonBlock(detail.manualReviewPayload.request)}
                      </pre>
                    </>
                  ) : null}
                  <Text fontSize="0.8rem" fontWeight="bold">
                    Response
                  </Text>
                  <pre
                    style={{
                      margin: '0.25rem 0 0',
                      fontSize: '11px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatJsonBlock(detail.manualReviewPayload.response)}
                  </pre>
                </View>
              </View>
            ) : (
              <Text marginTop="1rem">
                No stored Checkr Trust payload found for this manual review.
              </Text>
            )}

            <Heading level={6} marginTop="1rem">
              Check activity
            </Heading>
            {detail.checkActivity?.length ? (
              <ul>
                {detail.checkActivity.map((a) => (
                  <li key={a.id}>
                    {a.checkType}: {a.mvrStatus || a.checkStatus} ({a.checkId})
                  </li>
                ))}
              </ul>
            ) : (
              <Text>No activity rows</Text>
            )}

            {(detail.allowedActions || []).length ? (
              <TextField
                label="Fleet ID (for order/rerun MVR)"
                value={fleetId}
                onChange={(e) => setFleetId(e.target.value)}
                marginTop="1rem"
              />
            ) : null}

            <Flex direction="row" gap="0.5rem" wrap="wrap" marginTop="1rem">
              {(detail.allowedActions || []).map((action) => (
                <Button key={action} onClick={() => executeAction(action)}>
                  {ACTION_LABELS[action] || action}
                </Button>
              ))}
            </Flex>
            {detail.reviewQueueState === 'awaiting_system' &&
            !(detail.allowedActions || []).length ? (
              <Text marginTop="0.5rem" color="gray">
                Actions are disabled while this driver is queued for system processing.
                Use the Queued filter to find them again.
              </Text>
            ) : null}
            {!(detail.allowedActions || []).length &&
            detail.checkrMvrStatus &&
            !detail.checkrMvrStatus.startsWith('manual_check') &&
            !PROVISIONAL_ASYNC_STATUSES.has(detail.checkrMvrStatus) &&
            detail.checkrMvrStatus !== 'provisional_revoked' ? (
              <Text marginTop="0.5rem" color="gray">
                No manual actions for this status — system handles next steps automatically.
              </Text>
            ) : null}

            {confirm ? (
              <Alert variation="warning" marginTop="1rem">
                <Text>{confirm.label}</Text>
                <Flex direction="row" gap="0.5rem" marginTop="0.5rem">
                  <Button
                    variation="primary"
                    onClick={executeConfirm}
                    isLoading={actionLoading}
                  >
                    Confirm
                  </Button>
                  <Button onClick={() => setConfirm(null)} isDisabled={actionLoading}>
                    Cancel
                  </Button>
                </Flex>
              </Alert>
            ) : null}
          </View>
        ) : null}
      </Flex>
    </View>
  );
}
